import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addAccessUser, addUser } from '../../../services/user';

type RegisterNewUserModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onRegisterSuccess: (value: boolean) => void;
};

type RegisterData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
};

export const RegisterNewUserModal: React.FC<RegisterNewUserModalProps> = ({
  isOpen,
  onClose,
  onRegisterSuccess,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [registerData, setRegisterData] = useState<RegisterData>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [registerError, setRegisterError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setRegisterData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
      });
      setRegisterError('');
      setIsLoading(false);
      setShowPassword(false);
      setIsSuccess(false);
    }
  }, [isOpen]);

  const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleRegisterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setRegisterData((prev) => ({ ...prev, [name]: value }));
    if (registerError) {
      setRegisterError('');
    }
  };

  const validateRegisterForm = () => {
    if (!registerData.firstName.trim()) return 'Por favor ingresa tu nombre';
    if (!registerData.lastName.trim()) return 'Por favor ingresa tu apellido';
    if (!registerData.email.trim()) return 'Por favor ingresa tu email';
    if (!registerData.email.includes('@')) return 'Email invalido';
    if (!registerData.password || registerData.password.length < 6) return 'La contrasena debe tener al menos 6 caracteres';
    if (registerData.password !== registerData.confirmPassword) return 'Las contrasenas no coinciden';
    return '';
  };

  const handleRegister = async () => {
    const validationError = validateRegisterForm();
    if (validationError) {
      setRegisterError(validationError);
      return;
    }

    setIsLoading(true);

    try {
      const fullName = `${registerData.firstName} ${registerData.lastName}`.trim();
      const normalizedEmail = registerData.email.toLowerCase().trim();
      const username = normalizedEmail.split('@')[0] || normalizedEmail;

      const accessUserPayload = {
        fullName,
        username,
        email: normalizedEmail,
        password: registerData.password,
      };

      const profileUserPayload = {
        fullName,
        username,
        email: normalizedEmail,
        subscriptionPlanId: [],
      };

      const accessResponse: any = await addAccessUser(dispatch, accessUserPayload);

      if (accessResponse?.status === 201) {
        const profileResponse: any = await addUser(dispatch, profileUserPayload);

        if (profileResponse?.response) {
          setRegisterError('La cuenta de acceso fue creada, pero fallo el perfil. Por favor inicia sesion.');
          return;
        }

        setIsSuccess(true);
        onRegisterSuccess(true);
        return;
      }

      if (accessResponse?.response?.data?.message) {
        setRegisterError(accessResponse.response.data.message);
        return;
      }

      if (accessResponse?.response?.data) {
        setRegisterError(accessResponse.response.data);
        return;
      }

      setRegisterError('Error al crear la cuenta. Intenta con otro email.');
    } catch (error: any) {
      setRegisterError(error?.response?.data?.message || 'Error en el servidor');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="w-full max-w-xl bg-white rounded-3xl overflow-hidden shadow-2xl transform transition-transform duration-300 animate-fadeIn">
        <div className="relative bg-gradient-to-br from-sb-800 via-sb-700 to-sb-600 px-8 pt-8 pb-7 overflow-hidden">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center transition-all hover:bg-white/20 text-white/75 hover:text-white"
            aria-label="Cerrar modal"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M1 1L13 13M13 1L1 13" />
            </svg>
          </button>

          <div className="relative z-10">
            <div className="text-so-300 text-xs font-semibold tracking-widest uppercase mb-2">
              REGISTRO MUJER SOBERANA
            </div>
            <p className="text-white/80 text-sm font-light leading-relaxed max-w-sm">
              Crea tu cuenta para comenzar tu proceso de transformacion.
            </p>
          </div>
        </div>

        <div className="px-8 pt-7 pb-8 bg-white max-h-[75vh] overflow-y-auto">
          {isSuccess ? (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sb-100 flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#B14A36" strokeWidth="2">
                  <path d="M20 6L9 17L4 12" />
                </svg>
              </div>

              <h3 className="text-xl font-medium text-gray-900 mb-2">Cuenta creada</h3>
              <p className="text-sm text-gray-600 mb-6">Tu registro fue exitoso. Ahora puedes iniciar sesion.</p>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full py-3.5 bg-gradient-to-r from-so-600 to-so-400 text-white rounded-xl font-semibold text-sm tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                Ir a iniciar sesion
              </button>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-3 text-sm font-semibold text-sb-700 hover:text-sb-900 underline"
              >
                Ya tengo usuario
              </button>
            </div>
          ) : (
            <>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Crea tu cuenta</h3>
              <p className="text-sm text-gray-600 mb-6">
                Necesitamos algunos datos para crear tu cuenta de acceso seguro.
              </p>

              {registerError && (
                <div className="p-3 mb-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{registerError}</p>
                </div>
              )}

              <form className="space-y-4" onSubmit={(event) => event.preventDefault()}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">Nombre</label>
                    <input
                      type="text"
                      name="firstName"
                      value={registerData.firstName}
                      onChange={handleRegisterChange}
                      placeholder="Tu nombre"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">Apellido</label>
                    <input
                      type="text"
                      name="lastName"
                      value={registerData.lastName}
                      onChange={handleRegisterChange}
                      placeholder="Tu apellido"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    placeholder="tu@email.com"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">Contrasena</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={registerData.password}
                      onChange={handleRegisterChange}
                      placeholder="Min. 6 caracteres"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                    >
                      {showPassword ? 'Ocultar' : 'Ver'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">Confirmar contrasena</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={registerData.confirmPassword}
                    onChange={handleRegisterChange}
                    placeholder="Repite tu contrasena"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleRegister}
                  disabled={isLoading}
                  className="w-full py-3.5 bg-gradient-to-r from-so-600 to-so-400 text-white rounded-xl font-semibold text-sm tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creando cuenta...' : 'Crear cuenta'}
                </button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="text-sm font-semibold text-sb-700 hover:text-sb-900 underline"
                  >
                    Ya tengo usuario
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegisterNewUserModal;
